import { showErrorToast, showSuccessToast } from "@/components/toster";
import { useAuth } from "@/contexts/AuthContext";
import { useLoader } from "@/contexts/LoaderContext";
import useRequestToServer from "@/service/httpService";
import {
  authCallBack,
  generateAuthUrl,
  loginUrl,
} from "@/service/reqUrl.service";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import constant from "@/config/constant";
import { clearQueryCache } from "@/api/queryClient";
import { NEW_UI_HOME } from "@/components/daylight-insight/DaylightUiToggle";
import "./login.css";

const Login = () => {
  const { setAuth, saveDeviceId } = useAuth();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const code = queryParams.get("code");
  const state = queryParams.get("state");
  const { requestToServer } = useRequestToServer();

  const handleLogin = async () => {
    showLoader();
    try {
      const { data } = await requestToServer(
        generateAuthUrl,
        {},
        "POST",
        false
      );
      if (data.status === 200) {
        window.open(data.data, "_self");
      }
    } catch (error) {
      console.log(error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    document.title = "Sign in to Daylight";
    if (code) {
      showLoader();
      callAuthCallback(code, state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callAuthCallback = async (code: string, state: string) => {
    try {
      const { data } = await requestToServer(
        authCallBack,
        { authCode: code, state: state },
        "POST",
        false,
        "json",
        // Tag the callback with the DaylightPulse app-source so the
        // backend can skip the legacy Bluebird user-existence check —
        // Daylight identity is validated against the Daylight DB on the
        // subsequent /auth/login call.
        constant.appSources.daylight,
      );

      if (data.status !== 200) {
        hideLoader();
        showErrorToast(data?.message);
        return;
      }

      const initialToken = data.data.id_token;
      // Azure returns a refresh_token because the callback requests the
      // `offline_access` scope; keep it so the session can be renewed silently.
      const refreshToken = data.data.refresh_token;
      await loginToDaylightPulse(initialToken, refreshToken);
    } catch (error) {
      hideLoader();
      console.log(error);
    }
  };

  const loginToDaylightPulse = async (idToken: string, refreshToken?: string) => {
    try {
      const backendResponse = await fetch(
        `${import.meta.env.VITE_API_URL}${loginUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
            "Request-Type": "login",
            "app-source": constant.appSources.daylight,
          },
          body: JSON.stringify({
            confirmLogout: true,
            Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }
      );

      localStorage.removeItem("selectedEnvValid");
      localStorage.removeItem("selectedCustValid");

      const loginData = await backendResponse.json();
      if (loginData.status === 200) {
        const userData = loginData.data;
        // Defence against a same-tab user switch that bypassed `logout()` (e.g.
        // navigating straight to /login). `setAuth` never sees an email — the
        // user object it takes carries name/roles/RoleMapId only — so the
        // comparison has to happen here, against the `loggedEmail` written below
        // by the PREVIOUS session.
        const previousEmail = localStorage.getItem("loggedEmail");
        if (previousEmail && userData.Email && previousEmail !== userData.Email) {
          clearQueryCache();
        }
        setAuth(
          userData.IdToken,
          constant.appSources.daylight,
          userData.userDetails,
          {
            name: userData.Name,
            customerName: userData.CustomerName ?? undefined,
            roles: userData.roles,
            RoleMapId: userData.RoleMapId,
            isFinanceApprovalMandatory: userData?.isFinanceApprovalMandatory,
          },
          refreshToken
        );
        saveDeviceId(loginData.data.DeviceId);
        if (userData.OrgDataSourceId) {
          localStorage.setItem("OrgDataSourceId", userData.OrgDataSourceId);
        }
        if (userData.Email) {
          localStorage.setItem("loggedEmail", userData.Email);
        }
        showSuccessToast("Logged In Successfully");
        navigate(NEW_UI_HOME, { replace: true });
      } else {
        showErrorToast(loginData?.message || loginData?.error);
      }
    } catch (error) {
      console.log(error);
    } finally {
      hideLoader();
    }
  };

  return (
    <main className="login-page">
      <div className="login-sunrise" aria-hidden="true" />
      <section className="login-card" aria-labelledby="login-title">
        <div className="login-brand" aria-label="Daylight by Skyra.AI">
          <DaylightMark />
          <div className="login-wordmark">
            Day<span>Light</span>
          </div>
          <div className="login-byline">by Skyra.AI</div>
        </div>

        <div className="login-copy">
          <p className="login-eyebrow">Operations, brought into focus</p>
          <h1 id="login-title">Welcome back</h1>
          <p>Sign in with your organization account to continue to Daylight.</p>
        </div>

        <button type="button" className="login-button" onClick={handleLogin}>
          <MicrosoftMark />
          Continue with Microsoft
          <ArrowIcon />
        </button>

        <div className="login-trust">
          <LockIcon />
          <span>Secure access for authorized team members</span>
        </div>
      </section>
      <p className="login-footer">
        Daylight keeps your operational view clear, current, and considered.
      </p>
    </main>
  );
};

function DaylightMark() {
  return (
    <span className="login-logo" aria-hidden="true">
      <span className="login-logo-ring" />
      <span className="login-logo-core" />
    </span>
  );
}

function MicrosoftMark() {
  return (
    <svg className="login-ms-icon" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M1 1h7v7H1zM10 1h7v7h-7zM1 10h7v7H1zM10 10h7v7h-7z" fill="currentColor" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className="login-arrow" viewBox="0 0 20 20" aria-hidden="true">
      <path d="m7 4 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3.5" y="7" width="9" height="6.5" rx="2" fill="none" stroke="currentColor" />
      <path d="M5.5 7V5.25a2.5 2.5 0 0 1 5 0V7" fill="none" stroke="currentColor" strokeLinecap="round" />
    </svg>
  );
}

export default Login;
